import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { ExternalLinkIcon, KeyRoundIcon, PencilIcon } from 'lucide-react'
import { useCallback, useState } from 'react'

import { ApiError } from '@/api/errors'
import { portalSecretsApi } from '@/api/secrets'
import type { PortalSecret } from '@/api/types'
import { CopyButton } from '@/components/copy-button'
import { EmptyState } from '@/components/empty-state'
import { FormError } from '@/components/field-error'
import { Frame } from '@/components/frame'
import { Markdown } from '@/components/markdown'
import { PageHeader } from '@/components/page-header'
import {
  CorrectSecretValueDialog,
  PortalSecretRequests,
  PortalSecretRequestSheet,
} from '@/components/portal/portal-secret-requests'
import { SecretValueActions } from '@/components/secrets/secret-value-actions'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { enteredByLine, PORTAL_SECRETS_KEY } from '@/hooks/use-portal-secrets'
import { msg } from '@/i18n'
import { matching, readSearch } from '@/lib/search-params'
import { SECRET_KIND_LABELS, SECRET_VALUE_NOUN } from '@/lib/secrets'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/portal/acces')({
  validateSearch: (search: Record<string, unknown>) =>
    readSearch(
      {
        /** 1.83.0 : l'accès que vise la cloche ou le courriel, mis en évidence. */
        secret: matching(/^[0-9a-f]{24}$/),
        /** 1.84.0 : la demande d'accès à saisir — jamais la valeur dans l'adresse. */
        demande: matching(/^[0-9a-f]{24}$/),
      },
      search,
    ),
  component: PortalSharedSecretsPage,
})

/**
 * « Accès partagés » (lot 33) : les secrets que l'équipe partage avec le
 * client — staging, clés, jetons —, rangés par projet. La valeur ne s'y lit
 * que par Copier ou Afficher, et chaque lecture est notée.
 */
function PortalSharedSecretsPage() {
  const { secret: targetId, demande } = Route.useSearch()
  const navigate = Route.useNavigate()
  const secrets = useQuery({
    queryKey: PORTAL_SECRETS_KEY,
    queryFn: () => portalSecretsApi.list(),
  })
  const t = msg().portalRequests.access
  const groups = new Map<string, { title: string; items: PortalSecret[] }>()
  for (const secret of secrets.data ?? []) {
    const key = secret.project?.id ?? ''
    const title = secret.project?.name ?? t.allProjects
    groups.set(key, { title, items: [...(groups.get(key)?.items ?? []), secret] })
  }
  const ordered = [...groups.entries()].sort(([a], [b]) => (a === '' ? -1 : b === '' ? 1 : 0))
  // Le lien vise un accès que ce contact ne lit plus (ou plus du tout) : on le dit.
  const missing =
    targetId !== undefined &&
    secrets.isSuccess &&
    !secrets.data.some((secret) => secret.id === targetId)

  return (
    <div className="space-y-5">
      <PageHeader title={msg().portalShell.nav.access} />
      {missing && (
        <p role="status" className="text-sm text-muted-foreground" data-testid="acces-introuvable">
          {t.missing}
        </p>
      )}
      {secrets.isPending ? (
        <Frame>
          <Skeleton className="h-32 w-full rounded-md" />
        </Frame>
      ) : secrets.isError ? (
        <Frame>
          <FormError>
            {secrets.error instanceof ApiError ? secrets.error.message : t.failed}
          </FormError>
        </Frame>
      ) : secrets.data.length === 0 ? (
        <>
          <PortalSecretRequests />
          <Frame>
            <EmptyState icon={KeyRoundIcon} title={t.emptyTitle} description={t.emptyDescription} />
          </Frame>
        </>
      ) : (
        <>
          <PortalSecretRequests />
          {ordered.map(([key, group]) => (
            <Frame key={key || 'client'}>
              <h2 className="mb-3 font-semibold tracking-tight">{group.title}</h2>
              <ul className="divide-y" data-testid="acces-partages">
                {group.items.map((secret) => (
                  <SharedSecret
                    key={secret.id}
                    secret={secret}
                    highlighted={secret.id === targetId}
                  />
                ))}
              </ul>
            </Frame>
          ))}
        </>
      )}
      <PortalSecretRequestSheet
        requestId={demande}
        onClose={() => {
          void navigate({ search: (previous) => ({ ...previous, demande: undefined }) })
        }}
      />
    </div>
  )
}

function SharedSecret({ secret, highlighted }: { secret: PortalSecret; highlighted: boolean }) {
  const t = msg().portalRequests.access
  const [correcting, setCorrecting] = useState(false)
  const entered = enteredByLine(secret)
  // L'accès visé par la cloche : amené à l'écran une fois, à l'arrivée.
  const scrollTo = useCallback(
    (node: HTMLLIElement | null) => {
      if (highlighted && node) node.scrollIntoView({ block: 'center' })
    },
    [highlighted],
  )
  return (
    <li
      ref={scrollTo}
      className={cn(
        'space-y-2 py-3 first:pt-0 last:pb-0',
        highlighted && '-mx-3 rounded-md px-3 ring-2 ring-primary first:pt-3 last:pb-3',
      )}
      aria-current={highlighted ? 'true' : undefined}
      data-testid="acces"
    >
      <div className="flex flex-wrap items-center gap-2">
        <KeyRoundIcon className="size-4 text-primary" aria-hidden="true" />
        <span className="font-medium">{secret.label}</span>
        <Badge variant="secondary">{SECRET_KIND_LABELS[secret.kind]}</Badge>
        {entered && (
          <span className="text-xs text-muted-foreground" data-testid="acces-saisi-par">
            {entered}
          </span>
        )}
      </div>
      {(secret.username ?? secret.url) && (
        <p className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
          {secret.username && (
            <span className="inline-flex items-center gap-1">
              <span className="text-muted-foreground">{t.username}</span>
              <span className="font-mono">{secret.username}</span>
              <CopyButton value={secret.username} label={t.copyUsername} iconOnly variant="ghost" />
            </span>
          )}
          {secret.url &&
            (/^https?:\/\//i.test(secret.url) ? (
              <a
                href={secret.url}
                target="_blank"
                rel="noreferrer noopener"
                className="inline-flex items-center gap-1 break-all text-primary underline-offset-2 hover:underline"
              >
                {secret.url}
                <ExternalLinkIcon className="size-3.5" aria-hidden="true" />
              </a>
            ) : (
              <span className="font-mono break-all">{secret.url}</span>
            ))}
        </p>
      )}
      {secret.notes && (
        <Markdown className="text-sm text-muted-foreground">{secret.notes}</Markdown>
      )}
      {secret.public_part && (
        <div className="flex items-start gap-2">
          <pre className="max-h-24 min-w-0 flex-1 overflow-auto rounded-md bg-muted px-3 py-2 font-mono text-xs break-all whitespace-pre-wrap">
            {secret.public_part}
          </pre>
          <CopyButton value={secret.public_part} label={t.copyPublic} iconOnly />
        </div>
      )}
      <SecretValueActions
        kind={secret.kind}
        valueSet={secret.value_set}
        copyLabel={t.copyValue(SECRET_VALUE_NOUN[secret.kind])}
        // Noté au journal des lectures, que lit l'équipe : en français.
        context="Accès partagés"
        reveal={(payload) => portalSecretsApi.reveal(secret.id, payload)}
      />
      {secret.can_update_value && (
        <>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => {
              setCorrecting(true)
            }}
          >
            <PencilIcon />
            {t.correct}
          </Button>
          <CorrectSecretValueDialog
            secret={secret}
            open={correcting}
            onOpenChange={setCorrecting}
          />
        </>
      )}
    </li>
  )
}
